import type { LayoutType } from "@/domain/tableList/layout"
import type {
  FieldsParamOrderby,
  Order,
  SearchParamOrderby,
  SearchParamProps,
} from "@/utils/filters/types"
import { useCallback, useMemo } from "react"
import { useSearchParams } from "react-router-dom"
import { isNotBlank, isNullOrUndefined } from "./../utils/ts-utils"

type GetSearchParams = {
  [k: string]: boolean | number | string | (boolean | number | string)[]
} & {
  [o: `order[${string}]`]: Order
  search?: string
  page?: number
  itemsPerPage?: number
}

type WithLayout<TParam> = TParam & { layout?: LayoutType }

const isMultiple = (k: string) => k.substring(k.length - 2) === "[]"

const isValueValid = (str?: unknown): str is boolean | number | string => {
  return (
    (typeof str === "string" && isNotBlank(str) && str !== "NaN") ||
    typeof str === "boolean" ||
    (typeof str === "number" && !Number.isNaN(Number(str)))
  )
}
const isValueUsable = (
  str?: unknown
): str is boolean | number | string | (boolean | number | string)[] => {
  if (isNullOrUndefined(str)) return false

  if (Array.isArray(str)) {
    if (str.length < 1) return false
    for (const value of str) {
      if (!isValueValid(value)) return false
    }
    return true
  }

  return isValueValid(str)
}

const setURLSearchParam = (
  searchParams: URLSearchParams,
  key: string,
  value?: string | string[] | unknown
) => {
  const nSP = new URLSearchParams(searchParams)
  nSP.delete(key)

  if (!isValueUsable(value)) return nSP

  if (isMultiple(key)) {
    if (Array.isArray(value)) {
      value.map(v => nSP.append(key, v.toString()))
    } else {
      console.log(`Key '${key}' : '${value}' has to be converted into array`)
      nSP.append(key, value.toString())
    }
  } else {
    if (Array.isArray(value)) {
      console.log(`Key '${key}' : '${value}' has to be converted into string`)
      nSP.append(key, value.join(","))
    } else {
      nSP.set(key, value.toString())
    }
  }

  return nSP
}

const setURLSearchParams = <TParams extends GetSearchParams>(
  searchParams: URLSearchParams,
  p?: TParams
) => {
  let nSP = new URLSearchParams(searchParams)
  if (isNullOrUndefined(p)) return nSP

  Object.entries(p).map(([k, v]) => {
    nSP = setURLSearchParam(nSP, k, v)
  })

  return nSP
}

/* export const useGetOnResetRegistered = () => {
  const [onResetFunctions, setResetFuncs] = useState<{ [id: string]: () => void }>({})
  // const onResetFunctions = useMemo(() => onResetFunctions, [onResetFunctions])

  const onResetRegister = useCallback(
    (id: string, func: () => void) => {
      console.log("register", id, isNotNullNorUndefined(onResetFunctions[id]), func)
      if (isNotNullNorUndefined(onResetFunctions[id])) return
      const f = { ...onResetFunctions }
      f[id] = func
      setResetFuncs(f)
      console.log("registered ?", f, onResetFunctions)
    },
    [onResetFunctions]
  )

  const onResetUnregister = useCallback(
    (id: string) => {
      const f = { ...onResetFunctions }
      delete f[id]
      setResetFuncs(f)
    },
    [onResetFunctions]
  )

  return { onResetFunctions, onResetRegister, onResetUnregister }
}*/

export const useGetSearchParamsState = <
  TParams extends GetSearchParams,
  TProps extends SearchParamProps<TParams> = SearchParamProps<TParams>,
  TPOrders extends SearchParamOrderby<TParams> = SearchParamOrderby<TParams>,
  TFOrders extends FieldsParamOrderby<TParams> = FieldsParamOrderby<TParams>,
>() => {
  const [searchParams, setSearchParams] = useSearchParams()

  const setParams = useCallback(
    (params?: WithLayout<TParams>) => {
      const newUSP = setURLSearchParams(searchParams, params)
      setSearchParams(newUSP)
    },
    [searchParams, setSearchParams]
  )

  const setProperty = useCallback(
    (key: TProps, value: TParams[TProps]) => {
      const newUSP = setURLSearchParam(searchParams, key as string, value)
      setSearchParams(newUSP)
    },
    [searchParams, setSearchParams]
  )

  const setOrder = useCallback(
    (key: TFOrders, value: Order) => {
      const k = `order[${key}]` as string
      const newUSP = setURLSearchParam(searchParams, k, value)
      setSearchParams(newUSP)
    },
    [searchParams, setSearchParams]
  )

  const setSearch = useCallback(
    (value: TParams["search"]) => {
      const newUSP = setURLSearchParam(searchParams, "search", value)
      setSearchParams(newUSP)
    },
    [searchParams, setSearchParams]
  )

  const setLayout = useCallback(
    (value: WithLayout<TParams>["layout"]) => {
      const newUSP = setURLSearchParam(searchParams, "layout", value)
      setSearchParams(newUSP)
    },
    [searchParams, setSearchParams]
  )

  const resetSearchParams = useCallback(() => {
    const newUSP = new URLSearchParams()
    setSearchParams(newUSP)
  }, [setSearchParams])

  const getSearchParams = useMemo(() => {
    const p = {} as TParams
    for (const key of searchParams.keys()) {
      if (key === "layout") continue
      p[key as TProps] = isMultiple(key)
        ? (searchParams.getAll(key) as NonNullable<TParams[TProps]>)
        : (searchParams.get(key) as NonNullable<TParams[TProps]>)
    }
    return p
  }, [searchParams])

  const getOrderParams = useMemo(() => {
    const p = {} as TParams
    for (const key of searchParams.keys()) {
      if (key.substring(0, 6) !== "order[") continue
      p[key as TPOrders] = searchParams.get(key) as NonNullable<TParams[TPOrders]>
    }
    // if (objectKeys(p).length < 1) return { "order[updated_at": "desc" }
    return p
  }, [searchParams])

  // if (isNotNullNorUndefined(p)) setParams(p)

  return {
    getSearchParams,
    getOrderParams,
    searchParams,
    setParams,
    setProperty,
    setOrder,
    setSearch,
    setLayout,
    resetSearchParams,
  }
}
